// Renders a regional grid of nationality links to a triple-key page.
// Used by Elite/LTR/DTV tier/segment/category pages to expose nationality variants.

import { VISA_NATIONALITIES, VISA_REGIONS } from "@/data/visa-nationalities";
import { Globe } from "lucide-react";

type Props = {
  /** function returning the destination path for a given nationality slug */
  hrefFor: (nationalitySlug: string) => string;
  lang: "th" | "en";
  titleTh?: string;
  titleEn?: string;
  introTh?: string;
  introEn?: string;
};

const REGION_LABEL_EN: Record<string, string> = {
  asia: "Asia",
  europe: "Europe",
  americas: "Americas",
  oceania: "Oceania",
  "middle-east": "Middle East",
  africa: "Africa",
};

export function NationalityGridLinks({
  hrefFor,
  lang,
  titleTh = "ดูข้อมูลตามสัญชาติ",
  titleEn = "Browse by nationality",
  introTh = "เลือกสัญชาติของคุณเพื่อดูข้อมูลที่ปรับเฉพาะ — เอกสาร เงื่อนไข และคำถามที่พบบ่อย",
  introEn = "Pick your nationality to see country-specific docs, conditions, and FAQs.",
}: Props) {
  return (
    <section className="bg-muted/30 px-4 py-12">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-extrabold">{lang === "th" ? titleTh : titleEn}</h2>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          {lang === "th" ? introTh : introEn}
        </p>
        <div className="mt-6 space-y-6">
          {VISA_REGIONS.map((r) => {
            const items = VISA_NATIONALITIES.filter((n) => n.region === r.id);
            if (items.length === 0) return null;
            return (
              <div key={r.id}>
                <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
                  {lang === "th" ? r.label : REGION_LABEL_EN[r.id]}
                </h3>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {items.map((n) => (
                    <a
                      key={n.slug}
                      href={hrefFor(n.slug)}
                      className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:border-primary hover:text-primary transition"
                    >
                      <span className="text-base">{n.flag}</span>
                      <span className="truncate font-medium">
                        {lang === "th" ? n.nameTh : n.demonymEn}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
